import type { ComponentProps } from 'react';
import type { LabelTone, Tone } from './tone';

import { Badge as ShadcnBadge, badgeVariants } from './shadcn/badge';

/** The app-shaped badge, composed from the shadcn/ui `Badge` in `./shadcn/badge.tsx`.
 *
 *  Only the vocabulary is this file's. The app colours a badge by TONE — the shared `Tone` union that the
 *  timeline, the event stream and the dashboard signals all read from `./tone.ts` — where shadcn names a
 *  badge by role. The two are not the same axis: `success` and `warning` are tones with no shadcn variant
 *  at all, and every tone here is the soft form rather than shadcn's solid one. This map goes away with
 *  the wrapper in a later phase.
 *
 *  `default` and `muted` both resolved to `secondary`, which made the quiet tone a synonym rather than a
 *  step down — a caller asking for less emphasis got exactly the same chip, so `tone="muted"` documented
 *  an intention the UI never carried out. They are now the two neutral shadcn variants, and which way
 *  round follows from what the tones mean: `muted` keeps the filled chip with MUTED ink, which is the
 *  quieter of the two and the paint both tones used to share, and `default` takes the outlined chip with
 *  full-strength ink. The ordinary badge is the one that has to be read (a plugin id, a model's context
 *  window); the muted one is the one allowed to recede into the row.
 *
 *  The `LabelTone`s are not states: they map one to one onto the soft `label-N` variants over the chart
 *  series, so a register can tell its labels apart without borrowing a state colour. */
const TONE_VARIANT = {
  default: 'outline',
  muted: 'secondary',
  accent: 'soft-primary',
  danger: 'soft-destructive',
  success: 'soft-success',
  warning: 'soft-warning',
  'solid-accent': 'default',
  'label-1': 'label-1',
  'label-2': 'label-2',
  'label-3': 'label-3',
  'label-4': 'label-4',
  'label-5': 'label-5',
  'label-6': 'label-6',
} as const satisfies Record<Tone | LabelTone | 'solid-accent', NonNullable<Parameters<typeof badgeVariants>[0]>['variant']>;

/** `className` carries placement and density, never colour: the telemetry rail wants the page's chip one
 *  step smaller and pinned right, and inside a column layout a badge without it stretches to the full
 *  width of the section. Tone stays the only way to paint one. */
export function Badge({ tone = 'default', outline = false, className = '', ...props }: Omit<ComponentProps<typeof ShadcnBadge>, 'variant'> & { tone?: Tone | LabelTone | 'solid-accent'; outline?: boolean }) {
  return <ShadcnBadge variant={TONE_VARIANT[tone]} className={`${outline ? 'bg-transparent' : ''} ${className}`} {...props} />;
}
